import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';

import { useScanPickupResolution } from '../hooks/useScanPickupResolution';
import { useCreatePayment } from '../hooks/useCreatePayment';
import { usePaymentMethods } from '../hooks/usePaymentMethods';
import { markWorkItemCollected } from '../services/workItemDataService';
import { formatStatusDisplay } from '../components/workitems/utils/workItemUtils';
import './MobilePickupPage.css';

const formatMoney = (value: number): string =>
  value.toLocaleString('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 2,
  });

const todayIsoDate = (): string => {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

type CollectPhase = 'idle' | 'collecting' | 'collected' | 'error';

/**
 * Mobile-first pickup surface — the headline scan-to-workflow slice
 * (docs/qr-scan-workflow-requirements.md §4.1 rows 1f/1g). Reached by scanning a
 * ticket label (native camera → this deep link) on a phone. Mounted OUTSIDE
 * MainLayout so it carries no desktop chrome. Reuses the real payment hooks.
 *
 * Mark collected is offered with money still owing — after a warning, which
 * the audit row records (§9, D16: warn, don't block, any staff). A job
 * already collected goes to its summary instead.
 */
export function MobilePickupPage(): React.ReactElement {
  const { ticketNumber: ticketParam } = useParams<{ ticketNumber: string }>();
  const ticketNumber = ticketParam != null ? Number(ticketParam) : null;

  const { data, isLoading, error } = useScanPickupResolution(ticketNumber);

  const { methods, isLoading: methodsLoading, error: methodsError } = usePaymentMethods();
  const { phase, error: paymentError, submit, reset } = useCreatePayment();

  const [methodId, setMethodId] = useState('');
  const [amountStr, setAmountStr] = useState('');
  const [refNum, setRefNum] = useState('');
  const [paymentDate] = useState(todayIsoDate());
  // What was paid on this visit. A payment only settles the job when it covers
  // everything owing — a partial one, or paying the first of several invoices,
  // leaves the rest owing and the warn-before-collecting path in force (D16).
  const [paidTotal, setPaidTotal] = useState(0);
  const submittedAmount = useRef(0);

  const [collectPhase, setCollectPhase] = useState<CollectPhase>('idle');
  const [collectError, setCollectError] = useState<string | null>(null);
  const [collectNote, setCollectNote] = useState('');
  const [warnOpen, setWarnOpen] = useState(false);
  const navigate = useNavigate();

  // Seed the amount field once the invoice resolves.
  useEffect(() => {
    if (data?.invoice) setAmountStr(data.invoice.balanceDue.toFixed(2));
    reset();
    setPaidTotal(0);
    setCollectPhase('idle');
    setCollectError(null);
  }, [data?.invoice, reset]);

  // Payment confirmed by the orchestrator (SignalR) → advance to collect.
  useEffect(() => {
    if (phase !== 'succeeded') return;
    const amount = submittedAmount.current;
    submittedAmount.current = 0;
    if (amount > 0) setPaidTotal((t) => t + amount);
    reset();
  }, [phase, reset]);

  const selectedMethod = useMemo(
    () => methods.find((m) => m.id === methodId) || null,
    [methods, methodId],
  );

  const invoiceRemaining = data?.invoice ? Math.max(0, data.invoice.balanceDue - paidTotal) : 0;
  const totalRemaining = data ? Math.max(0, data.balanceDue - paidTotal) : 0;
  const settled = !!data && (data.isSettled || totalRemaining <= 0.005);
  const paid = paidTotal > 0;

  // After a partial payment, offer the rest.
  useEffect(() => {
    if (paidTotal > 0 && invoiceRemaining > 0.005) setAmountStr(invoiceRemaining.toFixed(2));
  }, [paidTotal, invoiceRemaining]);
  const isSubmittingPayment = phase === 'submitting' || phase === 'accepted';

  const parsedAmount = Number(amountStr);
  const amountError = (() => {
    if (!data?.invoice) return null;
    if (amountStr.trim() === '') return 'Amount is required';
    if (!Number.isFinite(parsedAmount) || parsedAmount <= 0) return 'Enter a valid amount';
    if (parsedAmount > invoiceRemaining + 0.001) {
      return `Cannot exceed ${formatMoney(invoiceRemaining)}`;
    }
    return null;
  })();

  const handlePay = () => {
    if (!data?.invoice || !selectedMethod || amountError) return;
    submittedAmount.current = parsedAmount;
    submit(
      {
        rsInvoiceId: data.invoice.rsInvoiceId,
        rsCustomerId: data.invoice.rsCustomerId,
        paymentMethodName: selectedMethod.name,
        dvPaymentMethodId: selectedMethod.id,
        rsPaymentMethodId: selectedMethod.rsPaymentMethodId ?? undefined,
        amount: parsedAmount,
        refNum: refNum.trim() || undefined,
        paymentDate,
      },
      data.invoice.number,
    );
  };

  const handleCollect = async (balanceWarningShown: boolean) => {
    if (!data) return;
    setWarnOpen(false);
    setCollectPhase('collecting');
    setCollectError(null);
    try {
      await markWorkItemCollected(data.guid, {
        note: collectNote.trim() || undefined,
        balanceWarningShown,
      });
      setCollectPhase('collected');
    } catch (e) {
      setCollectError(e instanceof Error ? e.message : 'Failed to mark collected');
      setCollectPhase('error');
    }
  };

  // ---- render states -------------------------------------------------------

  if (isLoading) {
    return (
      <div className="pickup-page pickup-center">
        <CircularProgress />
        <p className="pickup-muted">Looking up ticket #{ticketNumber}…</p>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="pickup-page">
        <Alert severity="error">{error ?? 'Could not resolve this ticket.'}</Alert>
      </div>
    );
  }

  // Collected on an earlier visit: the summary says by whom, and can undo it.
  if (data.workItem.collectedAt && collectPhase === 'idle') {
    return <Navigate to={`/job/${data.ticketNumber}/summary`} replace />;
  }

  if (collectPhase === 'collected') {
    return (
      <div className="pickup-page pickup-center">
        <div className="pickup-tick">✓</div>
        <h1 className="pickup-done-title">Device collected</h1>
        <p className="pickup-muted">
          #{data.ticketNumber} · {data.customerLabel}
        </p>
        <Button variant="text" onClick={() => navigate(`/job/${data.ticketNumber}/summary`)}>
          View summary
        </Button>
      </div>
    );
  }

  const collectControls = (
    <>
      <TextField
        label="Handover note (optional)"
        value={collectNote}
        onChange={(e) => setCollectNote(e.target.value)}
        placeholder="e.g. Collected by partner"
        disabled={collectPhase === 'collecting'}
        fullWidth
        sx={{ mb: 2 }}
        inputProps={{ maxLength: 4000 }}
      />
      {collectError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {collectError}
        </Alert>
      )}
    </>
  );

  return (
    <div className="pickup-page">
      <header className="pickup-header">
        <div className="pickup-ticket">#{data.ticketNumber}</div>
        <div className="pickup-customer">{data.customerLabel}</div>
        {data.jobStatus && <span className="pickup-status">{formatStatusDisplay(data.jobStatus)}</span>}
      </header>

      {data.unpaidCount > 1 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          This ticket has {data.unpaidCount} unpaid invoices totalling{' '}
          {formatMoney(data.balanceDue)}. Taking payment for the first
          (#{data.invoice?.number}) here; consolidated multi-invoice checkout is
          a follow-up.
        </Alert>
      )}

      {!settled && !data.invoice && (
        <Alert severity="warning">
          There's an outstanding balance of {formatMoney(data.balanceDue)}, but the
          invoice couldn't be prepared for payment (customer link unresolved). Open
          the ticket on desktop to take payment.
        </Alert>
      )}

      {!settled && paid && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {formatMoney(paidTotal)} recorded. {formatMoney(totalRemaining)} is still owing.
        </Alert>
      )}

      {!settled && data.invoice && invoiceRemaining > 0.005 && (
        <section className="pickup-card">
          <div className="pickup-balance-label">Balance due</div>
          <div className="pickup-balance">{formatMoney(invoiceRemaining)}</div>

          {methodsError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              Could not load payment methods: {methodsError}
            </Alert>
          )}

          <FormControl fullWidth disabled={methodsLoading || isSubmittingPayment} sx={{ mb: 2 }}>
            <InputLabel id="pm-label">Payment method</InputLabel>
            <Select
              labelId="pm-label"
              label="Payment method"
              value={methodId}
              onChange={(e) => setMethodId(String(e.target.value))}
            >
              {methods.map((m) => (
                <MenuItem key={m.id} value={m.id}>
                  {m.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Amount"
            value={amountStr}
            onChange={(e) => setAmountStr(e.target.value)}
            error={amountError !== null}
            helperText={amountError ?? `Up to ${formatMoney(invoiceRemaining)}`}
            disabled={isSubmittingPayment}
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
              inputMode: 'decimal',
            }}
            fullWidth
            sx={{ mb: 2 }}
          />

          <TextField
            label="Reference (optional)"
            value={refNum}
            onChange={(e) => setRefNum(e.target.value)}
            placeholder="EFTPOS ref, cheque #, etc."
            disabled={isSubmittingPayment}
            fullWidth
            sx={{ mb: 2 }}
          />

          {paymentError && <Alert severity="error" sx={{ mb: 2 }}>{paymentError}</Alert>}

          <Button
            variant="contained"
            size="large"
            fullWidth
            onClick={handlePay}
            disabled={isSubmittingPayment || methodsLoading || !selectedMethod || amountError !== null}
            startIcon={isSubmittingPayment ? <CircularProgress size={18} /> : null}
          >
            {isSubmittingPayment ? 'Recording…' : `Take payment ${formatMoney(parsedAmount || 0)}`}
          </Button>
        </section>
      )}

      {settled && (
        <section className="pickup-card">
          <Alert severity="success" sx={{ mb: 2 }}>
            {paid ? 'Payment recorded.' : 'Nothing outstanding.'} Ready for collection.
          </Alert>

          {collectControls}

          <Button
            variant="contained"
            size="large"
            fullWidth
            color="success"
            onClick={() => handleCollect(false)}
            disabled={collectPhase === 'collecting'}
            startIcon={collectPhase === 'collecting' ? <CircularProgress size={18} /> : null}
          >
            {collectPhase === 'collecting' ? 'Marking…' : 'Mark collected'}
          </Button>
        </section>
      )}

      {!settled && (
        <section className="pickup-card pickup-owing">
          <div className="pickup-balance-label">Customer taking it without paying in full?</div>
          <div className="pickup-owing-spacer" />
          {collectControls}
          <Button
            variant="outlined"
            size="large"
            fullWidth
            color="warning"
            onClick={() => setWarnOpen(true)}
            disabled={collectPhase === 'collecting' || isSubmittingPayment}
            startIcon={collectPhase === 'collecting' ? <CircularProgress size={18} /> : null}
          >
            {collectPhase === 'collecting' ? 'Marking…' : 'Mark collected with balance owing'}
          </Button>
        </section>
      )}

      <Dialog open={warnOpen} onClose={() => setWarnOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>{formatMoney(totalRemaining)} is still owing</DialogTitle>
        <DialogContent>
          <DialogContentText>
            The device will be recorded as collected with this balance unpaid. The
            balance and your name go on the job's record.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWarnOpen(false)}>Cancel</Button>
          <Button color="warning" variant="contained" onClick={() => handleCollect(true)}>
            Mark collected
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
